import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SearchComponent from "../SearchComponent/SearchComponent";
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_QUERY_PARAM,
  SEARCH_RESULTS_ANCHOR_ID,
} from "../../pages/shared/constants";

/** Marks the sticky header, so the scroll below can measure it instead of assuming its height. */
export const HEADER_BAR_ATTR = "data-header-bar";

/** Long enough for iOS to finish dismissing the keyboard and settle the visual viewport. */
const KEYBOARD_DISMISS_MS = 150;

/**
 * The header's search field, and the only writer of the `?q=` param.
 *
 * The term lives in the URL rather than in a store so a search can be shared, bookmarked and
 * undone with the back button. `SearchComponent` stays presentational; everything that knows about
 * routing is here.
 */
const HeaderSearch: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const urlTerm = searchParams.get(SEARCH_QUERY_PARAM) ?? "";
  const [term, setTerm] = useState(urlTerm);
  const timerRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // The URL is also written from outside this field — a back/forward press, or a link into
  // `/?q=…`. When that happens the input has to follow, or it keeps showing a term the grid is no
  // longer filtered by.
  useEffect(() => {
    setTerm((current) => (current === urlTerm ? current : urlTerm));
  }, [urlTerm]);

  useEffect(() => () => window.clearTimeout(timerRef.current ?? undefined), []);

  const commit = (value: string) => {
    const trimmed = value.trim();

    // Typing from a product page is still a catalog search; take the shopper to the catalog and
    // bring the term along. This is a real navigation, so it *should* land in history.
    if (location.pathname !== "/") {
      navigate(trimmed ? `/?${SEARCH_QUERY_PARAM}=${encodeURIComponent(trimmed)}` : "/");
      return;
    }

    const next = new URLSearchParams(searchParams);
    if (trimmed) {
      next.set(SEARCH_QUERY_PARAM, trimmed);
    } else {
      // Deleted rather than left empty, so clearing the field returns to a clean `/` instead of
      // leaving a `?q=` that looks like a search for nothing.
      next.delete(SEARCH_QUERY_PARAM);
    }
    // `replace` because otherwise every debounced keystroke becomes a history entry and the back
    // button walks the shopper backwards through their own typing.
    setSearchParams(next, { replace: true });
  };

  const handleChange = (value: string) => {
    setTerm(value);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => commit(value), SEARCH_DEBOUNCE_MS);
  };

  /**
   * Enter, or the "search" key on a phone keyboard.
   *
   * On a phone the keyboard covers roughly half the screen, so the results the shopper just asked
   * for are behind it. Dismissing it is only half the job — the grid also sits below the banner and
   * the services row, so without the scroll they close the keyboard onto the same view they
   * started from.
   */
  const handleSubmit = () => {
    // The field's own value rather than `term`: at submit time the DOM input is the authority.
    // They agree for ordinary typing, but a paste, an autofill or an IME composition can land in
    // the input without React having re-rendered yet, and then state is a character or a whole
    // word behind.
    const submitted = inputRef.current?.value ?? term;

    // The shopper has said they are done typing, so do not make them wait out the debounce.
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setTerm(submitted);
    commit(submitted);

    // Closing the keyboard is a side effect of the field losing focus; there is no API for it.
    // Read the header now, while the input is still a reliable way to find it.
    const header = inputRef.current?.closest(`[${HEADER_BAR_ATTR}]`) ?? null;
    inputRef.current?.blur();

    if (!submitted.trim()) return;

    // Deferred because dismissing the keyboard resizes the visual viewport on iOS. Scrolling in
    // the same tick measures against the old, shorter viewport and lands in the wrong place.
    window.setTimeout(() => {
      const results = document.getElementById(SEARCH_RESULTS_ANCHOR_ID);
      if (!results) return;

      // Measured rather than hardcoded: the sticky header is 103px on desktop and ~116px on
      // mobile, where it carries the search field on a second row.
      const headerHeight = header?.getBoundingClientRect().height ?? 0;
      const top = results.getBoundingClientRect().top + window.scrollY - headerHeight;

      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    }, KEYBOARD_DISMISS_MS);
  };

  return (
    <SearchComponent
      inputRef={inputRef}
      value={term}
      onChange={handleChange}
      onSubmit={handleSubmit}
    />
  );
};

export default HeaderSearch;
